import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Import manuell erstellter Angebots-PDFs (Verkauf → Angebote).
 * Der Server liest die Kopfdaten aus dem PDF-Text (Vorschlag), der Nutzer
 * prüft/korrigiert im Dialog und bestätigt — erst dann entsteht der Beleg.
 */

export const quoteImportCurrencySchema = z.enum(['EUR', 'USD', 'CHF']);
export type QuoteImportCurrency = z.infer<typeof quoteImportCurrencySchema>;

/** Aus dem PDF-Text erkannte Kopfdaten — alles nullable, der Nutzer entscheidet. */
export const quotePdfSuggestionSchema = z.object({
  /** Angebotsnummer (z. B. „26052633"). */
  number: z.string().nullable(),
  /** Belegdatum als ISO-Datum (JJJJ-MM-TT). */
  documentDate: z.string().nullable(),
  /** „Gültig bis" als ISO-Datum — steht oft im AGB-/Bedingungen-PDF („Gültigkeit"). */
  validUntil: z.string().nullable(),
  currency: quoteImportCurrencySchema.nullable(),
  language: z.enum(['DE', 'EN']),
  /** Kunden-/Anlagenname unterhalb des Datums (z. B. „KA Beggen"). */
  customerName: z.string().nullable(),
  /** Betreffzeile(n) des Angebots (z. B. „2 Stk. ATEX-0 Räumer …"). */
  subject: z.string().nullable(),
  /** Erkannte Gesamtsumme (netto) in Mikro-Einheiten. */
  netTotal: moneySchema.nullable(),
});
export type QuotePdfSuggestion = z.infer<typeof quotePdfSuggestionSchema>;

export const quoteImportCustomerMatchSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  number: z.string().nullable(),
  city: z.string().nullable(),
});
export type QuoteImportCustomerMatch = z.infer<typeof quoteImportCustomerMatchSchema>;

export const quoteImportParseResponseSchema = z.object({
  suggestion: quotePdfSuggestionSchema,
  /** CRM-Kunden, die zum erkannten Namen passen (Vorschläge für die Zuordnung). */
  customerMatches: z.array(quoteImportCustomerMatchSchema),
  /** Bestehendes Angebot mit derselben Nummer — Duplikat-Warnung im Dialog. */
  duplicateDocumentId: z.string().uuid().nullable(),
});
export type QuoteImportParseResponse = z.infer<typeof quoteImportParseResponseSchema>;

/** Import bestätigen: Angebot mit der Original-Nummer aus dem PDF anlegen. */
export const importQuoteSchema = z.object({
  customerId: z.string().uuid(),
  /** Original-Angebotsnummer aus dem PDF — wird als Belegnummer übernommen. */
  number: z.string().trim().min(1).max(40),
  documentDate: z.string().datetime(),
  validUntil: z.string().datetime().optional(),
  currency: quoteImportCurrencySchema.default('EUR'),
  language: z.enum(['DE', 'EN']).default('DE'),
  /** Angebotssumme (netto) — wird als eine Pauschal-Position übernommen. */
  netTotal: moneySchema.optional(),
  projectId: z.string().uuid().optional(),
  /** Betreffzeile über der Positionstabelle (aus dem PDF-Titel). */
  referenceText: z.string().trim().max(500).optional(),
  notes: z.string().trim().max(5000).optional(),
});
export type ImportQuoteInput = z.infer<typeof importQuoteSchema>;
